import { useCallback, useEffect, useRef } from "react";
import { Icon } from "./Icon";

/**
 * Rich text editor for composing.
 *
 * Built on contenteditable + document.execCommand. execCommand is deprecated
 * and every browser still implements it, because the replacement (a manual
 * Selection/Range implementation) is thousands of lines and gets undo wrong.
 * For a mail composer — bold, italic, lists, quote, link — it is the right
 * amount of machinery. If this ever needs tables and images it should become a
 * real editor library rather than growing.
 *
 * The value is HTML. Compose derives a text/plain alternative from it so every
 * message goes out multipart, which is what makes mail render in clients that
 * refuse HTML and in notification previews.
 */

interface Command {
  icon: string;
  title: string;
  command: string;
  value?: string;
  /** Shown as the shortcut hint; the browser implements these natively. */
  keys?: string;
}

const COMMANDS: (Command | "sep")[] = [
  { icon: "bold", title: "Bold", command: "bold", keys: "Ctrl+B" },
  { icon: "italic", title: "Italic", command: "italic", keys: "Ctrl+I" },
  { icon: "underline", title: "Underline", command: "underline", keys: "Ctrl+U" },
  { icon: "strikethrough", title: "Strikethrough", command: "strikeThrough" },
  "sep",
  { icon: "list", title: "Bulleted list", command: "insertUnorderedList" },
  { icon: "list-ordered", title: "Numbered list", command: "insertOrderedList" },
  { icon: "quote", title: "Quote", command: "formatBlock", value: "blockquote" },
  { icon: "code", title: "Code", command: "formatBlock", value: "pre" },
  "sep",
  { icon: "remove-formatting", title: "Clear formatting", command: "removeFormat" },
];

export function Editor({
  value, onChange, onSend, onInsertImage, placeholder,
}: {
  value: string;
  onChange: (html: string) => void;
  onSend: () => void;
  /**
   * Uploads a pasted or dropped image and returns the id to reference it by.
   * Returning null means the upload failed and nothing should be inserted —
   * an image the recipient cannot see is worse than an explicit failure.
   */
  onInsertImage: (file: File) => Promise<{ id: string; url: string } | null>;
  placeholder: string;
}) {
  const host = useRef<HTMLDivElement>(null);

  /**
   * Puts an image where the caret is.
   *
   * The `src` is a local object URL so the writer sees it immediately; the
   * `data-inline-id` is what the send path rewrites into a `cid:` reference.
   * Keeping those separate is what lets the composer show a real preview
   * without the draft ever containing a URL the recipient could not resolve.
   */
  const insertImage = useCallback(async (file: File) => {
    const placeholderId = `pending-${Math.random().toString(36).slice(2)}`;
    document.execCommand(
      "insertHTML",
      false,
      `<img data-pending="${placeholderId}" alt="${file.name.replace(/"/g, "")}" style="max-width:100%;opacity:.4">`,
    );
    onChange(host.current?.innerHTML ?? "");

    const uploaded = await onInsertImage(file);
    const node = host.current?.querySelector<HTMLImageElement>(`img[data-pending="${placeholderId}"]`);
    if (!node) return; // the writer deleted it while it was uploading
    if (!uploaded) {
      node.remove();
    } else {
      node.removeAttribute("data-pending");
      node.style.opacity = "";
      node.dataset.inlineId = uploaded.id;
      node.src = uploaded.url;
    }
    onChange(host.current?.innerHTML ?? "");
  }, [onChange, onInsertImage]);

  /** Images from a paste or a drop, in the order they arrived. */
  function imagesIn(data: DataTransfer | null): File[] {
    if (!data) return [];
    return Array.from(data.files).filter((f) => f.type.startsWith("image/"));
  }

  // Only write into the DOM when the value came from outside (a draft being
  // restored, say). Writing back what the user just typed would collapse the
  // caret to the start on every keystroke.
  useEffect(() => {
    const node = host.current;
    if (node && node.innerHTML !== value) node.innerHTML = value;
  }, [value]);

  const exec = useCallback((cmd: Command) => {
    host.current?.focus();
    document.execCommand(cmd.command, false, cmd.value);
    onChange(host.current?.innerHTML ?? "");
  }, [onChange]);

  function addLink() {
    const url = window.prompt("Link to:");
    if (!url) return;
    // Anything without a scheme is almost always a bare domain someone typed.
    const href = /^[a-z][a-z0-9+.-]*:/i.test(url) ? url : `https://${url}`;
    host.current?.focus();
    document.execCommand("createLink", false, href);
    onChange(host.current?.innerHTML ?? "");
  }

  return (
    <>
      <div className="cmp-tools" role="toolbar" aria-label="Formatting">
        {COMMANDS.map((cmd, i) =>
          cmd === "sep" ? (
            <span className="tsep" key={`sep${i}`} />
          ) : (
            <button
              key={cmd.command + (cmd.value ?? "")}
              className="tbtn2"
              title={cmd.keys ? `${cmd.title} (${cmd.keys})` : cmd.title}
              // Buttons must not take focus, or the selection they act on is
              // gone by the time the handler runs.
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => exec(cmd)}
            >
              <Icon name={cmd.icon} />
            </button>
          ),
        )}
        <button className="tbtn2" title="Insert link" onMouseDown={(e) => e.preventDefault()} onClick={addLink}>
          <Icon name="link" />
        </button>
      </div>

      <div
        ref={host}
        className="cmp-body"
        contentEditable
        suppressContentEditableWarning
        role="textbox"
        aria-multiline="true"
        aria-label="Message body"
        data-placeholder={placeholder}
        onInput={(e) => onChange((e.target as HTMLDivElement).innerHTML)}
        onKeyDown={(e) => {
          if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
            e.preventDefault();
            onSend();
          }
        }}
        onDragOver={(e) => {
          if (imagesIn(e.dataTransfer).length) e.preventDefault();
        }}
        onDrop={(e) => {
          const images = imagesIn(e.dataTransfer);
          if (!images.length) return;
          e.preventDefault();
          host.current?.focus();
          images.forEach((file) => void insertImage(file));
        }}
        onPaste={(e) => {
          // A screenshot on the clipboard is the common case; handle it before
          // looking at markup, or it pastes as nothing at all.
          const images = imagesIn(e.clipboardData);
          if (images.length) {
            e.preventDefault();
            images.forEach((file) => void insertImage(file));
            return;
          }

          // Pasting from a web page drags in its stylesheet-dependent markup,
          // which then travels to the recipient looking nothing like it did.
          // Take the HTML the source offered but keep only what mail supports.
          const html = e.clipboardData.getData("text/html");
          if (!html) return; // plain text pastes fine on its own
          e.preventDefault();
          const clean = html
            .replace(/<!--[\s\S]*?-->/g, "")
            .replace(/<(script|style|meta|link)[\s\S]*?<\/\1>/gi, "")
            .replace(/\son\w+="[^"]*"/gi, "")
            .replace(/\sclass="[^"]*"/gi, "");
          document.execCommand("insertHTML", false, clean);
          onChange(host.current?.innerHTML ?? "");
        }}
      />
    </>
  );
}
